/**
 * VenueManagerDialog — iter226
 *
 * Group-level CRUD for venues (courts, halls, etc.) with either a single flat
 * fee OR day/night/weekend time-band pricing. Sessions can be linked to a
 * venue so the "Who paid?" flow pre-fills the reimbursement amount.
 *
 * Props:
 *   open           — bool
 *   groupId        — string
 *   isAdmin        — bool
 *   onClose        — () => void
 *   onChanged      — () => void  (called after any add/edit/delete)
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Edit3, Trash2, Save, X, MapPin, Clock, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const EMPTY = {
  name: '',
  notes: '',
  has_time_bands: false,
  default_fee: '',
  day_fee: '',
  night_fee: '',
  weekend_fee: '',
};

export default function VenueManagerDialog({ open, groupId, isAdmin, onClose, onChanged }) {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // venue.id or 'new' or null
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // iter228 — Analytics section
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [aFrom, setAFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [aTo, setATo] = useState(() => new Date().toISOString().slice(0, 10));

  const loadAnalytics = useCallback(async () => {
    if (!groupId) return;
    setAnalyticsLoading(true);
    try {
      const params = new URLSearchParams();
      if (aFrom) params.set('from', aFrom);
      if (aTo) params.set('to', aTo);
      const r = await api.get(`/groups/${groupId}/venues/analytics?${params.toString()}`);
      setAnalytics(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analytics failed');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [groupId, aFrom, aTo]);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const r = await api.get(`/groups/${groupId}/venues`);
      setVenues(r.data?.venues || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load venues');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (open) load();
    else { setEditing(null); setDraft(EMPTY); }
  }, [open, load]);

  const startNew = () => {
    setDraft(EMPTY);
    setEditing('new');
  };

  const startEdit = (venue) => {
    setDraft({
      name: venue.name || '',
      notes: venue.notes || '',
      has_time_bands: !!venue.has_time_bands,
      default_fee: venue.default_fee != null ? String(venue.default_fee) : '',
      day_fee: venue.day_fee != null ? String(venue.day_fee) : '',
      night_fee: venue.night_fee != null ? String(venue.night_fee) : '',
      weekend_fee: venue.weekend_fee != null ? String(venue.weekend_fee) : '',
    });
    setEditing(venue.id);
  };

  const cancelEdit = () => { setEditing(null); setDraft(EMPTY); };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('Venue name is required');
      return;
    }
    const body = {
      name: draft.name.trim(),
      notes: draft.notes.trim(),
      has_time_bands: !!draft.has_time_bands,
      default_fee: Number(draft.default_fee || 0),
      day_fee: draft.day_fee === '' ? null : Number(draft.day_fee),
      night_fee: draft.night_fee === '' ? null : Number(draft.night_fee),
      weekend_fee: draft.weekend_fee === '' ? null : Number(draft.weekend_fee),
    };
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post(`/groups/${groupId}/venues`, body);
        toast.success('Venue added');
      } else {
        await api.patch(`/venues/${editing}`, body);
        toast.success('Venue updated');
      }
      await load();
      onChanged?.();
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save venue');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (venue) => {
    if (!window.confirm(`Delete venue "${venue.name}"? Sessions linked to it keep their history.`)) return;
    try {
      await api.delete(`/venues/${venue.id}`);
      toast.success('Venue deleted');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const feeLabel = (v) => {
    if (v.has_time_bands) {
      const parts = [];
      if (v.day_fee != null) parts.push(`Day ₹${v.day_fee}`);
      if (v.night_fee != null) parts.push(`Night ₹${v.night_fee}`);
      if (v.weekend_fee != null) parts.push(`W/E ₹${v.weekend_fee}`);
      return parts.join(' · ') || '—';
    }
    return `₹${v.default_fee || 0}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="venue-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Venue Library
          </DialogTitle>
          <DialogDescription>
            Predefine your regular courts / halls with their pricing so the &quot;Who paid?&quot; flow can auto-fill the amount.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-2 gap-2">
              <Button
                size="sm"
                variant={showAnalytics ? 'default' : 'outline'}
                onClick={() => { setShowAnalytics((s) => !s); if (!analytics) loadAnalytics(); }}
                data-testid="venue-analytics-toggle"
              >
                <BarChart2 className="w-3.5 h-3.5 mr-1" /> {showAnalytics ? 'Hide' : 'Analytics'}
              </Button>
              {isAdmin && editing !== 'new' && (
                <Button size="sm" onClick={startNew} data-testid="venue-add-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Venue
                </Button>
              )}
            </div>

            {showAnalytics && (
              <div className="border-2 border-primary/20 rounded-lg p-3 mb-3 bg-muted/20" data-testid="venue-analytics-panel">
                <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                  <BarChart2 className="w-3.5 h-3.5" /> Venue Spend
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <Input type="date" value={aFrom} onChange={(e) => setAFrom(e.target.value)} className="h-7 text-xs flex-1" data-testid="venue-analytics-from" />
                  <span className="text-xs text-muted-foreground">→</span>
                  <Input type="date" value={aTo} onChange={(e) => setATo(e.target.value)} className="h-7 text-xs flex-1" data-testid="venue-analytics-to" />
                  <Button size="sm" onClick={loadAnalytics} disabled={analyticsLoading} className="h-7 text-xs" data-testid="venue-analytics-refresh">
                    {analyticsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Run'}
                  </Button>
                </div>
                {analytics && (
                  <>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="bg-background border rounded p-2">
                        <p className="text-xs text-muted-foreground uppercase">Total Spend</p>
                        <p className="text-lg font-bold" data-testid="venue-analytics-total">₹{Number(analytics.total_spend || 0).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="bg-background border rounded p-2">
                        <p className="text-xs text-muted-foreground uppercase">Sessions</p>
                        <p className="text-lg font-bold">{analytics.total_sessions || 0}</p>
                      </div>
                    </div>
                    {(analytics.by_venue || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        No venue-linked reimbursements in this window.
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {analytics.by_venue.map((r) => (
                          <div key={r.venue_id} className="flex items-center justify-between text-xs bg-background border rounded px-2 py-1.5" data-testid={`venue-analytics-row-${r.venue_id}`}>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold truncate">{r.venue_name}</p>
                              <p className="text-xs text-muted-foreground">{r.sessions} session{r.sessions === 1 ? '' : 's'}</p>
                            </div>
                            <p className="font-bold">₹{Number(r.total || 0).toLocaleString('en-IN')}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {editing === 'new' && (
              <VenueForm
                draft={draft} setDraft={setDraft}
                onSave={save} onCancel={cancelEdit} saving={saving}
                testId="venue-form-new"
              />
            )}

            {venues.length === 0 && editing !== 'new' && (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="venues-empty-state">
                No venues yet. Add one so sessions can auto-fill the payment amount.
              </p>
            )}

            <div className="space-y-2 mt-2">
              {venues.map((v) => (
                editing === v.id ? (
                  <VenueForm
                    key={v.id}
                    draft={draft} setDraft={setDraft}
                    onSave={save} onCancel={cancelEdit} saving={saving}
                    testId={`venue-form-${v.id}`}
                  />
                ) : (
                  <div
                    key={v.id}
                    className="border rounded-lg p-3 flex items-start justify-between gap-2"
                    data-testid={`venue-row-${v.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{v.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        {v.has_time_bands && <Clock className="w-3 h-3" />}
                        {feeLabel(v)}
                      </p>
                      {v.notes && (
                        <p className="text-xs text-muted-foreground italic mt-1">{v.notes}</p>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(v)} data-testid={`venue-edit-${v.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(v)} className="text-red-500" data-testid={`venue-delete-${v.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function VenueForm({ draft, setDraft, onSave, onCancel, saving, testId }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div className="border-2 border-primary/30 rounded-lg p-3 space-y-3" data-testid={testId}>
      <div>
        <Label className="text-xs">Name *</Label>
        <Input
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. Dev D — Court 3"
          data-testid="venue-form-name"
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label className="text-xs">Time-band pricing</Label>
          <p className="text-xs text-muted-foreground">
            OFF = single flat fee. ON = separate Day / Night / Weekend rates.
          </p>
        </div>
        <Switch
          checked={draft.has_time_bands}
          onCheckedChange={(v) => set('has_time_bands', v)}
          data-testid="venue-form-timebands"
        />
      </div>

      {draft.has_time_bands ? (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Day (₹)</Label>
            <Input type="number" value={draft.day_fee} onChange={(e) => set('day_fee', e.target.value)} placeholder="—" data-testid="venue-form-day-fee" />
          </div>
          <div>
            <Label className="text-xs">Night (₹)</Label>
            <Input type="number" value={draft.night_fee} onChange={(e) => set('night_fee', e.target.value)} placeholder="—" data-testid="venue-form-night-fee" />
          </div>
          <div>
            <Label className="text-xs">Weekend (₹)</Label>
            <Input type="number" value={draft.weekend_fee} onChange={(e) => set('weekend_fee', e.target.value)} placeholder="—" data-testid="venue-form-weekend-fee" />
          </div>
        </div>
      ) : (
        <div>
          <Label className="text-xs">Flat fee (₹)</Label>
          <Input type="number" value={draft.default_fee} onChange={(e) => set('default_fee', e.target.value)} placeholder="0" data-testid="venue-form-default-fee" />
        </div>
      )}

      <div>
        <Label className="text-xs">Notes</Label>
        <Textarea
          rows={2}
          value={draft.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Optional — court number, contact, etc."
          data-testid="venue-form-notes"
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving} data-testid="venue-form-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="venue-form-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}
